import { AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  formatMismatchFieldLine,
  getDisplayableMismatchFields,
  getIdentityMismatchSeverity,
} from '@/lib/identityMismatch';
import type { IdentityMismatch } from '@/types';

interface Props {
  mismatch: IdentityMismatch;
  className?: string;
}

export default function IdentityMismatchPanel({ mismatch, className }: Props) {
  const { t } = useLanguage();
  const displayFields = getDisplayableMismatchFields(mismatch.fields);
  const severity = getIdentityMismatchSeverity(mismatch);

  if (displayFields.length === 0 || !severity) return null;

  const isCritical = severity === 'critical';
  const Icon = isCritical ? AlertTriangle : Info;

  return (
    <Card
      className={cn(
        isCritical
          ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-blue-500/30 bg-blue-500/5',
        className,
      )}
    >
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start gap-2">
          <Icon
            className={cn(
              'h-4 w-4 shrink-0 mt-0.5',
              isCritical
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-blue-600 dark:text-blue-400',
            )}
          />
          <div className="space-y-1 min-w-0">
            <p
              className={cn(
                'text-sm font-medium',
                isCritical
                  ? 'text-amber-800 dark:text-amber-200'
                  : 'text-blue-900 dark:text-blue-100',
              )}
            >
              {t(
                isCritical
                  ? 'application.identityMismatch.panelTitle'
                  : 'application.identityMismatch.panelTitleContact',
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {t(
                isCritical
                  ? 'application.identityMismatch.panelIntro'
                  : 'application.identityMismatch.panelIntroContact',
              )}
            </p>
          </div>
        </div>

        <ul className="space-y-1.5 pl-6 text-sm">
          {displayFields.map((field) => (
            <li key={field.field} className="text-foreground">
              {formatMismatchFieldLine(field, t)}
            </li>
          ))}
        </ul>

        <p className="text-xs text-muted-foreground pl-6">
          {t('application.identityMismatch.panelFooter')}
        </p>
      </CardContent>
    </Card>
  );
}
